/* eslint-disable no-unused-vars */
import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { manageCampaignsAPI } from 'services/manageCampaigns.service';
import {
  generalActions,
  targetingPlacementsActions,
  formErrorsCampaign_action,
} from 'store/actions';
import { useDebounce } from 'utils';
import { useSignal } from '@preact/signals-react';

const createDataArrFromNumbers = ({
  allApps,
  faundedApps,
  data,
  current_selected_apps,
  appsSelectedData$,
}) => {
  // const faunded_apps = faundedApps ? [...faundedApps] : [];
  // const connectAllApps = [...allApps, ...current_selected_apps, ...faunded_apps];
  // let result = [];
  // data.forEach((number) => {
  //   const new_item = connectAllApps.find((app) => app.pba_number === number);
  //   result.push(new_item);
  //   // result = [...result, new_item].map((app) => {
  //   //   return { pba_number: app.pba_number, pba_name: app.pba_name };
  //   // });
  // });
  // return result;
  const appMap = new Map(
    [...allApps, ...current_selected_apps, ...(faundedApps ?? []), ...appsSelectedData$.value].map(
      (app) => [app.pba_number, app],
    ),
  );
  return data.map((number) => appMap.get(number)).filter(Boolean);
};

const checkString = (victim, search) => {
  return victim?.toString().toLowerCase().includes(search.toString().toLowerCase());
};

const checkArray = (arr, search) => {
  return arr?.some((item) => item.toLowerCase().includes(search));
};

export const useFacade = (allApps = [], appsGroups, close) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const defaultApps = useMemo(() => allApps?.map(({ pba_id }) => pba_id), [allApps]);
  const current_selected_apps = useSelector(
    (store) => store?.general?.mainCampaignObject?.currently_selected_apps,
  );
  const [stateSet] = useState(() => new Set(defaultApps));

  const appsSelectedData$ = useSignal([]);

  const isContained = useCallback((id) => stateSet.has(id), [stateSet]);

  const [
    fetchSearch,
    {
      data: faundedApps,
      isError,
      isFetching: faundedAppsIsFatching,
      isLoading: faundedAppsIsLoading,
      isSuccess: faundedAppsIsSuccess,
    },
  ] = manageCampaignsAPI.useLazyGetSearchAppsNewQuery();

  const onSaveHandler = useCallback(
    (data) => {
      const newData = createDataArrFromNumbers({
        allApps,
        faundedApps,
        data,
        current_selected_apps,
        appsSelectedData$,
      });
      newData.sort((a, b) => a.pba_name.localeCompare(b.pba_name));
      dispatch(generalActions.update_currently_selected_apps(newData));
      if (newData.length) {
        dispatch(
          targetingPlacementsActions.updateErrorField({
            errorField: 'placements',
            newValue: false,
          }),
        );
        dispatch(
          formErrorsCampaign_action.updateErrorField({
            errorField: 'targeting',
            newValue: false,
          }),
        );
      }

      close();
    },
    [allApps, faundedApps, current_selected_apps, appsSelectedData$.value],
  );

  const filteredFaundedApps = useMemo(
    () => (faundedApps ? faundedApps.filter((app) => !isContained(app.pba_id)) : []),
    [faundedApps, isContained],
  );

  useEffect(() => {
    if (debouncedSearch.length > 3) {
      fetchSearch({
        cm_id: '',
        term: debouncedSearch,
      });
    }
  }, [debouncedSearch]);

  const foundedAppGroups = useMemo(() => {
    if (!search) return appsGroups;
    const trimmedSearch = search.trim();
    return appsGroups
      .map((group) => {
        if (checkString(group.group_name, trimmedSearch)) {
          const apps = group.apps.filter(
            (app) =>
              checkString(app.pba_name, trimmedSearch) ||
              checkString(app.pba_number, trimmedSearch) ||
              checkArray(app.app_types_names, trimmedSearch),
          );

          if (apps.length === 0) return { ...group };
          return { ...group, apps };
        } else {
          const apps = group.apps.filter(
            (app) =>
              checkString(app.pba_name, trimmedSearch) ||
              checkString(app.pba_number, trimmedSearch) ||
              checkArray(app.app_types_names, trimmedSearch),
          );
          if (apps.length) return { ...group, apps };
        }
        return null;
      })
      .filter(Boolean);
  }, [appsGroups, search]);

  const handleClearSearchValue = useCallback(() => {
    setSearch('');
  }, []);

  return {
    isContained,
    onSaveHandler,
    filteredFaundedApps,
    search,
    setSearch,
    foundedAppGroups,
    handleClearSearchValue,
    debouncedSearch,
    faundedAppsIsFatching,
    faundedAppsIsSuccess,
    appsSelectedData$,
  };
};
